import {
  UnwrapRef,
  reactive,
  ref,
  Ref,
  watch,
  ComponentInternalInstance,
  getCurrentInstance,
  unref,
  computed,
  ComputedRef,
} from '@vue/composition-api';

import { i18n_t } from '@/locales/i18n';
import extraFileInfoHandler from '@/renderer/components/SpacePage/compositions/extraFileInfoHandler';
import useImgPre from '@/renderer/components/SpacePage/compositions/useImagePreview';
import ipc from '@/renderer/scripts/utils/ipc';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';

type TableData = {
  list: SMH.RecentlyUsedFileItemData[];
  selectedList: SMH.RecentlyUsedFileItemData[];
  selectedRowKeys: Array<Number | String>;
};
type PageData = {
  currPage: number;
  pageSize: number;
  total: number;
  pageSizeOptions: Array<any>;
};
type Handler = {
  tableData: UnwrapRef<TableData>;
  pageData: UnwrapRef<PageData>;
  columns: ComputedRef<Array<any>>;
  loading: Ref<boolean>;
  loadingOnHeader: Ref<boolean>; // 在顶部loading，loading期间list内容不变
  methods: {
    pullList: () => Promise<void>;
    refresh: () => void;
    silentRefresh: () => void;
    onCurrentChange: (current: number) => void;
  };
};
export default function sendRecentUseListHandle(): Handler {
  // 当前组件
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const { methods: extraFileInfoMethods } = extraFileInfoHandler();
  const isMobileStyle = computed(() => vm.$store.getters.isMobileStyle);
  const generalColumns = [
    {
      colKey: 'name',
      title: i18n_t('项目名称'),
      width: 200,
      ellipsis: {
        disabled: true,
      },
    },
    {
      colKey: 'path',
      title: i18n_t('位置'),
      width: 130,
    },
    {
      colKey: 'visitTime',
      cell: 'visitTime',
      title: i18n_t('访问时间'),
      ellipsis: true,
      width: 150,
    },
    {
      colKey: 'size',
      title: i18n_t('大小'),
      width: 150,
      // 由于此栏承载了操作按钮组，因此宽度必须 > 150px
      fixed: 'right',
    },
  ];
  const mobileColumns = [
    {
      colKey: 'mobile-name',
      title: '',
      ellipsis: true,
      width: 180,
    },
    {
      colKey: 'mobile-operate',
      title: '',
      width: 70,
      align: 'right',
    },
  ];
  const columns = computed(() => (isMobileStyle.value ? mobileColumns : generalColumns));
  const pageData = reactive<PageData>({
    currPage: 1,
    pageSize: 10,
    total: 0,
    pageSizeOptions: [], // 值为[]即不显示
  });

  // const tableData = reactive<TableData>({ list: [] });
  const tableData = reactive<TableData>({
    list: [],
    selectedList: [],
    selectedRowKeys: [],
  });
  const loading = ref(false);
  const loadingOnHeader = ref(false);
  const methods = {
    async pullList(silentLoading = false) {
      const startTime = new Date().getTime();
      const opt = {
        page: pageData.currPage,
        pageSize: pageData.pageSize,
        orderBy: 'visitTime',
        orderByType: 'desc',
      };
      try {
        loading.value = !silentLoading;
        loadingOnHeader.value = silentLoading;
        const { data: listRes } = await smh.userAPI.getRecentlyUsedDirectory(opt);
        pageData.total = listRes.totalNum;
        ipc.emit(ipc.RENDERER_EVENTS_MAP.ON_DATA_LOADED, pageData.total);
        if (listRes.contents.length > 0) {
          const list = listRes.contents.map((item: any, index: number) => ({ ...item, id: index }));
          tableData.list = list;
          extraFileInfoMethods.getFileExtraRefresh(tableData, { multiSpaceId: true });
          const { getImagePre } = useImgPre();
          getImagePre(tableData, tableData.list, 0, 20, 20);
        }
        if (loadingOnHeader.value) {
          tips.success(i18n_t('刷新成功'));
        }
        loading.value = false;
        loadingOnHeader.value = false;
        reporter.report('list_cost_time', {
          page: 'RecentUse',
          cost: new Date().getTime() - startTime,
          count: pageData.pageSize,
        });
      } catch (err) {
        tips.error(err as MsgError);
      }
    },
    refresh() {
      tableData.list = [];
      tableData.selectedList = [];
      tableData.selectedRowKeys = [];
      methods.pullList();
    },
    silentRefresh() {
      if (!loading.value && !loadingOnHeader.value) {
        tableData.selectedRowKeys = [];
        tableData.selectedList = [];
        methods.pullList(true);
      }
    },
    onCurrentChange(current: number) {
      pageData.currPage = current;
      methods.pullList();
    },
  };
  methods.pullList();
  watch(
    () => pageData.total,
    val => {
      if (!val) return;
      const newPageNum = Math.ceil(val / pageData.pageSize);
      if (pageData.currPage > newPageNum) {
        pageData.currPage = newPageNum;
        methods.refresh();
      }
    },
  );
  watch(
    () => tableData.selectedList,
    newList => {
      ipc.emit(ipc.RENDERER_EVENTS_MAP.ON_SELECTED_CHANGE, unref(newList));
    },
  );
  return {
    columns,
    pageData,
    tableData,
    loading,
    loadingOnHeader,
    methods,
  };
}
